import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaShoppingCart, 
  FaHeart, 
  FaShare, 
  FaRulerCombined, 
  FaWater, 
  FaGlassWhiskey, 
  FaFish,
  FaBoxes,
  FaStar,
  FaCheck,
  FaPlus,
  FaMinus,
  FaFilter,
  FaThermometerHalf,
  FaLightbulb,
  FaTint,
  FaLeaf,
  FaCog,
  FaShieldAlt,
  FaTools,
  FaTimes,
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaClipboardList,
  FaEye
} from 'react-icons/fa';
import OrderDialog from "./OrderDialog";

const AquariumDetails = ({ aquarium = {} }) => {
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('details');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Default aquarium data if none provided
  const {
    id,
    name = "حوض سمك زجاجي فاخر",
    price = 15000,
    quantity: availableQuantity = 5,
    size = "30*30*30",
    available = true,
    material = "زجاج",
    type = "aquarium",
    capacity = "غير محدد",
    description = "حوض سمك زجاجي عالي الجودة مناسب للأسماك الاستوائية",
    images = ['/api/placeholder/500/400', '/api/placeholder/500/400', '/api/placeholder/500/400'],
    // Additional dynamic attributes
    thickness = "8mm",
    shape = "مستطيل",
    color = "شفاف",
    weight = "5kg",
    includes = "غطاء + قاعدة",
    warranty = "سنة واحدة"
  } = aquarium;

  // Sample accessories data
  const accessories = [
    {
      id: 'filter-1',
      name: 'فلتر داخلي قوي',
      price: 2500,
      category: 'تنقية',
      icon: <FaFilter />,
      description: 'فلتر داخلي عالي الجودة لتنظيف المياه',
      compatible: ['10*10*10', '20*20*20', '30*30*30'],
      image: '/api/placeholder/100/100'
    },
    {
      id: 'heater-1',
      name: 'سخان مياه أوتوماتيكي',
      price: 1800,
      category: 'تدفئة',
      icon: <FaThermometerHalf />,
      description: 'سخان مياه قابل للتحكم في درجة الحرارة',
      compatible: ['10*10*10', '20*20*20', '30*30*30'],
      image: '/api/placeholder/100/100'
    },
    {
      id: 'light-1',
      name: 'إضاءة LED ملونة',
      price: 3200,
      category: 'إضاءة',
      icon: <FaLightbulb />,
      description: 'إضاءة LED متعددة الألوان مع جهاز تحكم',
      compatible: ['10*10*10', '20*20*20', '30*30*30', '40*40*40'],
      image: '/api/placeholder/100/100'
    },
    {
      id: 'pump-1',
      name: 'مضخة هواء صامتة',
      price: 1500,
      category: 'تهوية',
      icon: <FaTint />,
      description: 'مضخة هواء صامتة لتوفير الأكسجين',
      compatible: ['10*10*10', '20*20*20', '30*30*30'],
      image: '/api/placeholder/100/100'
    },
    {
      id: 'plant-1',
      name: 'نباتات صناعية متنوعة',
      price: 800,
      category: 'ديكور',
      icon: <FaLeaf />,
      description: 'مجموعة نباتات صناعية جميلة',
      compatible: ['10*10*10', '20*20*20', '30*30*30', '40*40*40'],
      image: '/api/placeholder/100/100'
    }
  ];

  // Filter accessories based on aquarium size
  const compatibleAccessories = accessories.filter(acc => 
    acc.compatible.includes(size || '10*10*10')
  );

  const accessoryCategories = [...new Set(compatibleAccessories.map(acc => acc.category))];
  
  const toggleAccessory = (accessoryId) => {
    setSelectedAccessories(prev => 
      prev.includes(accessoryId) 
        ? prev.filter(id => id !== accessoryId)
        : [...prev, accessoryId]
    );
  };

  const calculateTotal = () => {
    const aquariumTotal = (price || 0) * quantity;
    const accessoriesTotal = selectedAccessories.reduce((total, accId) => {
      const accessory = accessories.find(acc => acc.id === accId);
      return total + (accessory?.price || 0);
    }, 0);
    return aquariumTotal + accessoriesTotal;
  };

  const handleOrder = () => {
    setShowOrderDialog(true);
  };

  const handleOrderConfirm = (orderWithCustomer) => {
    console.log('Order confirmed:', orderWithCustomer);
    alert('تم تأكيد طلبك بنجاح! سنتواصل معك قريباً.');
    setShowOrderDialog(false);
  };

  const filteredAccessories = selectedCategory 
    ? compatibleAccessories.filter(acc => acc.category === selectedCategory)
    : compatibleAccessories;

  const getMaterialIcon = (materialType) => {
    const materialStr = materialType.toLowerCase();
    if (materialStr.includes('زجاج') || materialStr.includes('glass')) return <FaGlassWhiskey />;
    if (materialStr.includes('أكريليك') || materialStr.includes('acrylic')) return <FaWater />;
    return <FaGlassWhiskey />;
  };

  const getMaterialColor = (materialType) => {
    const materialStr = materialType.toLowerCase();
    if (materialStr.includes('زجاج') || materialStr.includes('glass')) return '#06b6d4';
    if (materialStr.includes('أكريليك') || materialStr.includes('acrylic')) return '#3b82f6';
    return '#06b6d4';
  };

  // Dynamic attributes section
  const dynamicAttributes = [
    { name: "السماكة", value: thickness, icon: <FaRulerCombined /> },
    { name: "الشكل", value: shape, icon: <FaBoxes /> },
    { name: "اللون", value: color, icon: <FaTint /> },
    { name: "الوزن", value: weight, icon: <FaTools /> },
    { name: "المرفقات", value: includes, icon: <FaCheck /> },
    { name: "الضمان", value: warranty, icon: <FaShieldAlt /> }
  ];

  return (
    <>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        color: 'white',
        paddingBottom: '2rem'
      }}>
        {/* Header */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(51, 65, 85, 0.5)',
          padding: '1rem'
        }}>
          <div style={{
            maxWidth: '1280px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                background: 'rgba(6, 182, 212, 0.2)',
                border: '1px solid rgba(6, 182, 212, 0.4)',
                color: '#06b6d4',
                padding: '10px',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaArrowLeft size={16} />
              <span style={{ display: window.innerWidth > 640 ? 'inline' : 'none' }}>العودة</span>
            </button>

            <h1 style={{
              fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
              fontWeight: 'bold',
              color: '#06b6d4',
              margin: 0,
              textAlign: 'center',
              flex: 1,
              padding: '0 1rem'
            }}>
              تفاصيل الحوض
            </h1>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setIsLiked(!isLiked)}
                style={{
                  background: isLiked ? 'rgba(239, 68, 68, 0.2)' : 'rgba(51, 65, 85, 0.5)',
                  border: `1px solid ${isLiked ? 'rgba(239, 68, 68, 0.4)' : 'rgba(71, 85, 105, 0.5)'}`,
                  color: isLiked ? '#ef4444' : '#cbd5e1',
                  padding: '10px',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                <FaHeart size={16} />
              </button>
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: name,
                      text: `شاهد هذا الحوض الرائع: ${name}`,
                      url: window.location.href
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    alert('تم نسخ الرابط!');
                  }
                }}
                style={{
                  background: 'rgba(51, 65, 85, 0.5)',
                  border: '1px solid rgba(71, 85, 105, 0.5)',
                  color: '#cbd5e1',
                  padding: '10px',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                <FaShare size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '2rem 1rem'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            alignItems: 'start'
          }}>
            {/* Image Section */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.95)',
              borderRadius: '24px',
              padding: '2rem',
              border: '1px solid rgba(6, 182, 212, 0.2)'
            }}>
              <div style={{
                width: '100%',
                height: '400px',
                borderRadius: '16px',
                overflow: 'hidden',
                marginBottom: '1rem',
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))'
              }}>
                <img
                  src={images[selectedImageIndex]}
                  alt={name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
              
              {/* Thumbnail Images */}
              {images.length > 1 && (
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  overflowX: 'auto',
                  paddingBottom: '8px'
                }}>
                  {images.map((img, index) => (
                    <img
                      key={index}
                      src={img}
                      alt={`${name} ${index + 1}`}
                      onClick={() => setSelectedImageIndex(index)}
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        border: selectedImageIndex === index 
                          ? '2px solid #06b6d4' 
                          : '2px solid transparent',
                        opacity: selectedImageIndex === index ? 1 : 0.7
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Info Section */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.95)',
              borderRadius: '24px',
              padding: '2rem',
              border: '1px solid rgba(6, 182, 212, 0.2)'
            }}>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#06b6d4',
                marginBottom: '1rem'
              }}>
                {name}
              </h1>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <span style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#fbbf24'
                }}>
                  {price} DA
                </span>
                <span style={{
                  background: available 
                    ? 'rgba(34, 197, 94, 0.2)' 
                    : 'rgba(239, 68, 68, 0.2)',
                  color: available ? '#22c55e' : '#ef4444',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <FaStar size={12} />
                  {available ? 'متاح' : 'غير متاح'}
                </span>
              </div>

              {/* Specs Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <div style={{
                  background: 'rgba(6, 182, 212, 0.1)',
                  border: '1px solid rgba(6, 182, 212, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <FaRulerCombined size={24} style={{ color: '#06b6d4', marginBottom: '8px' }} />
                  <div style={{ fontSize: '14px', color: '#94a3b8' }}>الحجم</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{size}</div>
                </div>

                <div style={{
                  background: 'rgba(6, 182, 212, 0.1)',
                  border: '1px solid rgba(6, 182, 212, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <FaBoxes size={24} style={{ color: '#06b6d4', marginBottom: '8px' }} />
                  <div style={{ fontSize: '14px', color: '#94a3b8' }}>الكمية المتاحة</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{availableQuantity}</div>
                </div>

                <div style={{
                  background: 'rgba(6, 182, 212, 0.1)',
                  border: '1px solid rgba(6, 182, 212, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <FaGlassWhiskey size={24} style={{ color: getMaterialColor(material), marginBottom: '8px' }} />
                  <div style={{ fontSize: '14px', color: '#94a3b8' }}>المادة</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{material}</div>
                </div>

                <div style={{
                  background: 'rgba(6, 182, 212, 0.1)',
                  border: '1px solid rgba(6, 182, 212, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <FaFish size={24} style={{ color: '#06b6d4', marginBottom: '8px' }} />
                  <div style={{ fontSize: '14px', color: '#94a3b8' }}>النوع</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{type}</div>
                </div>
              </div>

              {/* Quantity Selector */}
              <div style={{
                background: 'rgba(6, 182, 212, 0.05)',
                border: '1px solid rgba(6, 182, 212, 0.2)',
                borderRadius: '16px',
                padding: '1.5rem',
                marginBottom: '2rem'
              }}>
                <h3 style={{
                  color: '#06b6d4',
                  marginBottom: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}>
                  اختر الكمية
                </h3>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem'
                }}>
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    style={{
                      background: quantity <= 1 ? 'rgba(71, 85, 105, 0.5)' : 'rgba(6, 182, 212, 0.2)',
                      border: '1px solid rgba(6, 182, 212, 0.4)',
                      color: quantity <= 1 ? '#64748b' : '#06b6d4',
                      padding: '12px',
                      borderRadius: '12px',
                      cursor: quantity <= 1 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaMinus size={16} />
                  </button>
                  
                  <div style={{
                    background: 'rgba(51, 65, 85, 0.5)',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: '#06b6d4',
                    minWidth: '60px',
                    textAlign: 'center'
                  }}>
                    {quantity}
                  </div>
                  
                  <button
                    onClick={() => setQuantity(Math.min(availableQuantity, quantity + 1))}
                    disabled={quantity >= availableQuantity}
                    style={{
                      background: quantity >= availableQuantity ? 'rgba(71, 85, 105, 0.5)' : 'rgba(6, 182, 212, 0.2)',
                      border: '1px solid rgba(6, 182, 212, 0.4)',
                      color: quantity >= availableQuantity ? '#64748b' : '#06b6d4',
                      padding: '12px',
                      borderRadius: '12px',
                      cursor: quantity >= availableQuantity ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaPlus size={16} />
                  </button>
                </div>
              </div>

              {/* Order Button */}
              <button
                onClick={handleOrder}
                disabled={!available}
                style={{
                  width: '100%',
                  background: available 
                    ? 'linear-gradient(135deg, #06b6d4, #0891b2)'
                    : 'rgba(71, 85, 105, 0.5)',
                  color: 'white',
                  border: 'none',
                  padding: '16px',
                  borderRadius: '16px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  cursor: available ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  marginBottom: '1rem'
                }}
              >
                <FaShoppingCart size={20} />
                {available ? `اطلب الآن - ${calculateTotal().toFixed(2)} DA` : 'غير متاح'}
              </button>
            </div>
          </div>

          {/* Tabs and Content */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.95)',
            borderRadius: '24px',
            padding: '2rem',
            border: '1px solid rgba(6, 182, 212, 0.2)',
            gridColumn: '1 / -1'
          }}>
            {/* Tab Headers */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '2rem',
              borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
              overflowX: 'auto'
            }}>
              {[
                { id: 'details', label: 'التفاصيل', icon: <FaClipboardList /> },
                { id: 'specs', label: 'المواصفات', icon: <FaRulerCombined /> },
                { id: 'accessories', label: 'الإكسسوارات', icon: <FaCog /> }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    background: activeTab === tab.id ? 'rgba(6, 182, 212, 0.2)' : 'transparent',
                    border: 'none',
                    color: activeTab === tab.id ? '#06b6d4' : '#94a3b8',
                    padding: '12px 24px',
                    borderRadius: '12px 12px 0 0',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                    borderBottom: activeTab === tab.id ? '2px solid #06b6d4' : '2px solid transparent',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'details' && (
              <div>
                <h3 style={{
                  color: '#06b6d4',
                  marginBottom: '1rem',
                  fontSize: '1.3rem',
                  fontWeight: 'bold'
                }}>
                  وصف المنتج
                </h3>
                <p style={{
                  color: '#cbd5e1',
                  lineHeight: '1.8',
                  marginBottom: '2rem'
                }}>
                  {description}
                </p>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1.5rem'
                }}>
                  <div style={{
                    background: 'rgba(6, 182, 212, 0.1)',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    borderRadius: '16px',
                    padding: '1.5rem'
                  }}>
                    <h4 style={{
                      color: '#06b6d4',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FaWater />
                      معلومات المياه
                    </h4>
                    <ul style={{
                      color: '#cbd5e1',
                      lineHeight: '1.6',
                      listStyle: 'none',
                      padding: 0
                    }}>
                      <li>• مناسب للمياه العذبة والمالحة</li>
                      <li>• يحتاج إلى تنظيف دوري</li>
                      <li>• درجة حرارة مثلى: 22-26°C</li>
                    </ul>
                  </div>

                  <div style={{
                    background: 'rgba(6, 182, 212, 0.1)',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    borderRadius: '16px',
                    padding: '1.5rem'
                  }}>
                    <h4 style={{
                      color: '#06b6d4',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FaShieldAlt />
                      ضمان الجودة
                    </h4>
                    <ul style={{
                      color: '#cbd5e1',
                      lineHeight: '1.6',
                      listStyle: 'none',
                      padding: 0
                    }}>
                      <li>• ضمان لمدة سنة واحدة</li>
                      <li>• {material} عالي الجودة</li>
                      <li>• مقاوم للكسر</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'specs' && (
              <div>
                <h3 style={{
                  color: '#06b6d4',
                  marginBottom: '1.5rem',
                  fontSize: '1.3rem',
                  fontWeight: 'bold'
                }}>
                  المواصفات الفنية
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1.5rem'
                }}>
                  {dynamicAttributes.map((attr, index) => (
                    <div key={index} style={{
                      background: 'rgba(6, 182, 212, 0.1)',
                      border: '1px solid rgba(6, 182, 212, 0.3)',
                      borderRadius: '16px',
                      padding: '1.5rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          color: '#06b6d4',
                          fontSize: '1.5rem'
                        }}>
                          {attr.icon}
                        </div>
                        <h4 style={{
                          color: '#06b6d4',
                          margin: 0,
                          fontSize: '1.1rem',
                          fontWeight: 'bold'
                        }}>
                          {attr.name}
                        </h4>
                      </div>
                      <div style={{
                        color: '#cbd5e1',
                        fontSize: '1rem',
                        lineHeight: '1.6'
                      }}>
                        {attr.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'accessories' && (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '2rem',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <h3 style={{
                    color: '#06b6d4',
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    margin: 0
                  }}>
                    الإكسسوارات المتاحة
                  </h3>
                  
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={{
                      background: 'rgba(51, 65, 85, 0.5)',
                      border: '1px solid rgba(6, 182, 212, 0.3)',
                      borderRadius: '12px',
                      padding: '8px 12px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">جميع الفئات</option>
                    {accessoryCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '1.5rem',
                  marginBottom: '2rem'
                }}>
                  {filteredAccessories.map(accessory => (
                    <div
                      key={accessory.id}
                      style={{
                        background: selectedAccessories.includes(accessory.id) 
                          ? 'rgba(6, 182, 212, 0.15)' 
                          : 'rgba(51, 65, 85, 0.5)',
                        border: selectedAccessories.includes(accessory.id)
                          ? '2px solid #06b6d4'
                          : '1px solid rgba(71, 85, 105, 0.5)',
                        borderRadius: '16px',
                        padding: '1.5rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => toggleAccessory(accessory.id)}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1rem'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <div style={{
                            color: '#06b6d4',
                            fontSize: '1.5rem'
                          }}>
                            {accessory.icon}
                          </div>
                          <div>
                            <h4 style={{
                              color: 'white',
                              fontSize: '1.1rem',
                              fontWeight: 'bold',
                              margin: 0
                            }}>
                              {accessory.name}
                            </h4>
                            <span style={{
                              color: '#06b6d4',
                              fontSize: '0.9rem',
                              background: 'rgba(6, 182, 212, 0.2)',
                              padding: '2px 8px',
                              borderRadius: '12px'
                            }}>
                              {accessory.category}
                            </span>
                          </div>
                        </div>
                        
                        {selectedAccessories.includes(accessory.id) && (
                          <div style={{
                            background: '#06b6d4',
                            color: 'white',
                            borderRadius: '50%',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <FaCheck size={12} />
                          </div>
                        )}
                      </div>
                      
                      <p style={{
                        color: '#cbd5e1',
                        fontSize: '0.9rem',
                        lineHeight: '1.5',
                        marginBottom: '1rem'
                      }}>
                        {accessory.description}
                      </p>
                      
                      <div style={{
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        color: '#fbbf24'
                      }}>
                        {accessory.price} DA
                      </div>
                    </div>
                  ))}
                </div>

                {selectedAccessories.length > 0 && (
                  <div style={{
                    background: 'rgba(6, 182, 212, 0.1)',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    borderRadius: '16px',
                    padding: '1.5rem'
                  }}>
                    <h4 style={{
                      color: '#06b6d4',
                      marginBottom: '1rem'
                    }}>
                      الإكسسوارات المحددة ({selectedAccessories.length})
                    </h4>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {selectedAccessories.map(accId => {
                        const accessory = accessories.find(acc => acc.id === accId);
                        return (
                          <span
                            key={accId}
                            style={{
                              background: 'rgba(6, 182, 212, 0.2)',
                              color: '#06b6d4',
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '0.9rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            {accessory?.name}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAccessory(accId);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#06b6d4',
                                cursor: 'pointer',
                                padding: '2px'
                              }}
                            >
                              <FaTimes size={12} />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Dialog */}
      <OrderDialog
        item={{
          id,
          name,
          price: parseFloat(price) || 0,
          type: "aquarium",
          quantity,
          size,
          material,
          accessories: selectedAccessories.map(accId => {
            const accessory = accessories.find(acc => acc.id === accId);
            return {
              id: accessory?.id,
              name: accessory?.name,
              price: accessory?.price,
              type: "accessory"
            };
          }),
          totalPrice: calculateTotal()
        }}
        onClose={() => setShowOrderDialog(false)}
      />
    </>
  );
};

export default AquariumDetails;